import type { UseNavigateResult } from "@tanstack/react-router";
import { Check } from "lucide-react";
import type { JSX } from "react";

import type {
	MultiPropertyObject,
	Property,
} from "@/components/generic/multi-property-card";

import type {
	LoginMachine,
	MachineInfo,
} from "@/data-interfaces/authentication";
import type {
	LibraryBlank,
	MagazineBlank,
	MagazineSlot,
} from "@/data-interfaces/blanks";
import {
	type ApiError,
	isApiError,
	isApiWaitingOrError,
} from "@/data-interfaces/generics";
import type { RealTimeData } from "@/data-interfaces/real-time-data";
import {
	LIBRARY_BLANK_DETAILS_PAGE_URL,
	MAGAZINE_SLOT_DETAILS_PAGE_URL,
} from "@/lib/page-routes";
import {
	getParsedSessionStorageItem,
	SSK_MACHINE,
	SSK_MACHINE_INFO,
} from "@/lib/session-storage-keys";
import { getFloatMillimetersString } from "@/lib/string-utils";

export const getMagazineBlankProperties = (
	magazineBlank: MagazineBlank,
): Array<Property> => {
	return [
		{ label: magazineBlank.name, title: "Name" },
		{
			label: getFloatMillimetersString(magazineBlank.thickness_meters),
			title: "Thickness",
		},
	];
};

export const getLibraryBlanksProperties = (
	libraryBlank: LibraryBlank,
): Array<Property> => {
	return [
		{
			label: getFloatMillimetersString(libraryBlank.thickness_meters),
			title: "Thickness",
		},
		{
			label: libraryBlank.insertion_date.toLocaleString(),
			title: "Insertion date",
		},
	];
};

const getActiveMagazineSlotFlag = (
	magazineSlotId: number,
	activeMagazineSlotId: number,
): JSX.Element | undefined => {
	if (magazineSlotId === activeMagazineSlotId) {
		return (
			<div className="h-12.5 w-12.5 flex items-center justify-center text-[#FFF394] p-1.25">
				<Check className="w-full h-full" />
			</div>
		);
	}

	return undefined;
};

const getActiveMagazineSlot = (
	realTimeData: RealTimeData | ApiError | null,
): number => {
	if (!isApiWaitingOrError(realTimeData)) {
		return realTimeData.magazine_slot ?? -1;
	}

	return -1;
};

export const getMagazineSlots = (
	magazineSlots: Array<MagazineSlot>,
	realTimeData: RealTimeData | ApiError | null,
	navigate: UseNavigateResult<string>,
): Array<MultiPropertyObject> => {
	const objects: Array<MultiPropertyObject> = [];

	for (const magSlot of magazineSlots) {
		if (magSlot.blank) {
			objects.push({
				title: `Position ${magSlot.id}`,
				properties: getMagazineBlankProperties(magSlot.blank),
				cardHeaderCustomComponent: getActiveMagazineSlotFlag(
					magSlot.id,
					getActiveMagazineSlot(realTimeData),
				),
				clickCallback: () => {
					navigate({
						to: MAGAZINE_SLOT_DETAILS_PAGE_URL,
						search: () => magSlot,
					});
				},
			});
		}
	}

	return objects;
};

export const getLibraryBlanks = (
	blanks: Array<LibraryBlank>,
	navigate: UseNavigateResult<string>,
): Array<MultiPropertyObject> => {
	const objects: Array<MultiPropertyObject> = [];

	for (const blank of blanks) {
		objects.push({
			title: blank.name,
			properties: getLibraryBlanksProperties(blank),
			clickCallback: () => {
				navigate({ to: LIBRARY_BLANK_DETAILS_PAGE_URL, search: () => blank });
			},
		});
	}

	return objects;
};

export const isMagazineVisibile = (): boolean => {
	const machine = getParsedSessionStorageItem<LoginMachine>(SSK_MACHINE);

	if (isApiError(machine)) {
		console.warn("Error on getting 'machine' key in session storage");
		return false;
	}

	return machine.has_magazine;
};

export const isLibraryVisibile = (): boolean => {
	const machine_info =
		getParsedSessionStorageItem<MachineInfo>(SSK_MACHINE_INFO);

	if (isApiError(machine_info)) {
		console.warn("Error on getting 'machine' key in session storage");
		return false;
	}

	return !machine_info.no_blank_names_mode;
};
